import { WEATHER } from "./config.ts";
import { hash2, smoothstep } from "./math.ts";

/** The room's weather at one moment. Presentation only: no simulation reads it. */
export interface WeatherSample {
  /** Wind 10 m above the sea, m/s. */
  windSpeed: number;
  /** Compass direction the wind blows towards, degrees in [0, 360) (0° = +z, 90° = +x). */
  windHeading: number;
  /** The room's mean wind heading, degrees; the wind stays within `WEATHER.headingRange` of it. */
  prevailingHeading: number;
  /** 0 fair … 1 full storm, from the wind speed. */
  storm: number;
}

export function createWeatherSample(): WeatherSample {
  return { windSpeed: WEATHER.windMin, windHeading: 0, prevailingHeading: 0, storm: 0 };
}

/** The room's fixed prevailing wind heading, degrees. */
export function prevailingHeading(seed: number): number {
  return hash2(-1, 7, seed) * 360;
}

/**
 * Weather of the room seeded `seed` at `seconds` of server time: every `WEATHER.period` seconds a
 * hashed keyframe picks a wind speed (gales rarer than breezes) and a veer off the prevailing
 * heading, and the wind eases between neighbouring keyframes. Pure, so every client agrees.
 */
export function weatherAt(seed: number, seconds: number, out: WeatherSample): WeatherSample {
  const s = seconds / WEATHER.period, i = Math.floor(s), f = smoothstep(0, 1, s - i);
  const speed = (key: number): number => WEATHER.windMin + (WEATHER.windMax - WEATHER.windMin) * hash2(key, 1, seed) ** 2;
  const veer = (key: number): number => WEATHER.headingRange * (2 * hash2(key, 2, seed) - 1);
  const prevailing = prevailingHeading(seed);
  out.prevailingHeading = prevailing;
  out.windSpeed = speed(i) + (speed(i + 1) - speed(i)) * f;
  const heading = prevailing + veer(i) + (veer(i + 1) - veer(i)) * f;
  out.windHeading = ((heading % 360) + 360) % 360;
  out.storm = smoothstep(WEATHER.stormStart, WEATHER.stormFull, out.windSpeed);
  return out;
}
